import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText } from 'lucide-react';
import { PRICE_UNIT_OPTIONS } from './constants';

export function ManualItemForm({ form, setForm }) {
  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Manual Entry Mode</p>
            <p className="text-xs text-amber-600">
              This item will be added without linking to Part Master. Useful for legacy orders or spot quotes.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Item Description *</Label>
        <Input
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          placeholder="e.g., M12x50 Hex Bolt Grade 8.8"
          data-testid="manual-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Part No / Item Code</Label>
          <Input
            value={form.part_internal_no}
            onChange={(e) => update('part_internal_no', e.target.value)}
            placeholder="e.g., BOLT-M12X50"
            className="font-mono"
          />
        </div>
        <div className="space-y-2">
          <Label>Weight (grams)</Label>
          <Input
            type="number"
            value={form.net_weight_grams}
            onChange={(e) => update('net_weight_grams', e.target.value)}
            placeholder="e.g., 45.5"
          />
        </div>
      </div>

      <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Unit Price *</Label>
            <Input
              type="number" step="0.01"
              value={form.unit_price || ''}
              onChange={(e) => update('unit_price', parseFloat(e.target.value) || 0)}
              placeholder="0.00" className="font-mono"
              data-testid="manual-price-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Price Per</Label>
            <Select value={String(form.price_unit)} onValueChange={(v) => update('price_unit', parseInt(v) || 1)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRICE_UNIT_OPTIONS.map(u => (
                  <SelectItem key={u.value} value={String(u.value)}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Quantity *</Label>
          <Input type="number" value={form.quantity} onChange={(e) => update('quantity', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="space-y-2">
          <Label>Unit (UOM)</Label>
          <Select value={form.quantity_unit} onValueChange={(v) => update('quantity_unit', v)}>
            <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pcs">Pieces (pcs)</SelectItem>
              <SelectItem value="kg">Kilograms (kg)</SelectItem>
              <SelectItem value="sets">Sets</SelectItem>
              <SelectItem value="lbs">Pounds (lbs)</SelectItem>
              <SelectItem value="custom">Custom...</SelectItem>
            </SelectContent>
          </Select>
          {form.quantity_unit === 'custom' && (
            <Input value={form.uom_text} onChange={(e) => update('uom_text', e.target.value)} placeholder="Enter custom unit (e.g., cartons)" className="mt-2" />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Mfg Lead Time</Label>
          <Input value={form.production_lead_time || ''} onChange={(e) => update('production_lead_time', e.target.value)} placeholder="e.g., 4-6 Weeks" />
        </div>
        <div className="space-y-2">
          <Label>Sample Lead Time</Label>
          <Input value={form.sample_lead_time || ''} onChange={(e) => update('sample_lead_time', e.target.value)} placeholder="e.g., 2 Weeks" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tooling Cost (One-Time)</Label>
          <Input type="number" value={form.tooling_cost} onChange={(e) => update('tooling_cost', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="space-y-2">
          <Label>Sample Cost (One-Time)</Label>
          <Input type="number" value={form.sample_cost} onChange={(e) => update('sample_cost', parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Remarks</Label>
        <Input value={form.remarks} onChange={(e) => update('remarks', e.target.value)} placeholder="Any additional notes..." />
      </div>
    </div>
  );
}
